import React, { createContext } from 'react';
import './styles/App.css';
import { Routes, Route, Link } from "react-router-dom";
import Home from './Pages/Home';
import NotFound from './Pages/NotFound';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import { SignInButton } from "./Components/Simple/SignInButton";
import { SignOutButton } from "./Components/Simple/SignOutButton";
import Unauthorized from './Pages/Unauthorized';
import IPAcute from './Pages/IPAcute';
import IPPysch from './Pages/IPPysch';
import IPRehab from './Pages/IPRehab';
import LTCH from './Pages/LTCH';
import HH from './Pages/HH';
import OPPS from './Pages/OPPS';
import PFS from './Pages/PFS';
import ESRD from './Pages/ESRD';
import Title from './Components/Simple/Title';
import IPAcuteResult from './Pages/IPAcuteResult';
import IPPyschResult from './Pages/IPPyschResult';
import IPRehabResult from './Pages/IPRehabResult';
import LTCHResult from './Pages/LTCHResult';
import HHResult from './Pages/HHResult';
import OPPSResult from './Pages/OPPSResult';
import PFSResult from './Pages/PFSResult';
import ESRDResult from './Pages/ESRDResult';
import SNF from './Pages/SNF';
import SNFResult from './Pages/SNFResult';
import { enableBatchDashboard, enableEOB, enableEnabledPricers, enableFQHCPricer } from './Utils/features';
import BatchSubmit from './Pages/BatchSubmit';
import EobGenerator from './Pages/EobGenerator';
import useModulesApi from './Hooks/useModulesApi';
import { EOB_VIEW } from './Constants/moduleNames';
import { REDIRECT } from './Constants/protectionBehaviors';
import Protect from './Components/Combined/Protect';
import FQHC from './Pages/FQHC';
import FQHCResult from './Pages/FQHCResult';
import {
	IPPS as IPPS_MODEL,
	PSYCH as PSYCH_MODEL,
	ESRD as ESRD_MODEL,
	HH as HH_MODEL,
	FQHC as FQHC_MODEL,
	LTCH as LTCH_MODEL,
	OPPS as OPPS_MODEL,
	PFS as PFS_MODEL,
	REHAB as REHAB_MODEL,
	SNF as SNF_MODEL
} from './Constants/modelTypes'; // alias to avoid conflicts with pages

const authEnabled = process.env.REACT_APP_ENABLE_AUTH === "1";

const ModulesContext = createContext({
	loading: true,
	success: false,
	error: null,
	modules: [],
	refresh: async () => { }
});

function App() {
	const modules = useModulesApi();
	// Convert all module names to lower case for case-insensitive comparison
	const lowerCaseModules = modules.modules.map(module => module.toLowerCase());

	const renderContent = () => {
		if (authEnabled) {
			return (
				<>
					<AuthenticatedTemplate>
						<ModulesContext.Provider value={modules}>
							{renderPricer()}
						</ModulesContext.Provider>
					</AuthenticatedTemplate>

					<UnauthenticatedTemplate>
						<>
							<Title />
							<div className="row justify-content-center">
								<div className="col-auto text-center">
									<SignInButton />
								</div>
							</div>
						</>
					</UnauthenticatedTemplate>
				</>
			)
		}
		return renderPricer();
	};

	const renderPricer = () => {
		if (enableEnabledPricers) {
			return (
				<Routes>
					<Route exact path="/" element={<Home />} />
					<Route exact path="/errors/unauthorized" element={<Unauthorized />} />
					{lowerCaseModules.includes(IPPS_MODEL.toLowerCase()) && <Route exact path="/pricer/ip/acute" element={<IPAcute />} />}
					{lowerCaseModules.includes(IPPS_MODEL.toLowerCase()) && <Route exact path="/pricer/ip/acute/result" element={<IPAcuteResult />} />}
					{lowerCaseModules.includes(PSYCH_MODEL.toLowerCase()) && <Route exact path="/pricer/ip/psych" element={<IPPysch />} />}
					{lowerCaseModules.includes(PSYCH_MODEL.toLowerCase()) && <Route exact path="/pricer/ip/psych/result" element={<IPPyschResult />} />}
					{lowerCaseModules.includes(REHAB_MODEL.toLowerCase()) && <Route exact path="/pricer/ip/rehab" element={<IPRehab />} />}
					{lowerCaseModules.includes(REHAB_MODEL.toLowerCase()) && <Route exact path="/pricer/ip/rehab/result" element={<IPRehabResult />} />}
					{lowerCaseModules.includes(LTCH_MODEL.toLowerCase()) && <Route exact path="/pricer/ltch" element={<LTCH />} />}
					{lowerCaseModules.includes(LTCH_MODEL.toLowerCase()) && <Route exact path="/pricer/ltch/result" element={<LTCHResult />} />}
					{lowerCaseModules.includes(OPPS_MODEL.toLowerCase()) && <Route exact path="/pricer/opps" element={<OPPS />} />}
					{lowerCaseModules.includes(OPPS_MODEL.toLowerCase()) && <Route exact path="/pricer/opps/result" element={<OPPSResult />} />}
					{lowerCaseModules.includes(PFS_MODEL.toLowerCase()) && <Route exact path="/pricer/pfs" element={<PFS />} />}
					{lowerCaseModules.includes(PFS_MODEL.toLowerCase()) && <Route exact path="/pricer/pfs/result" element={<PFSResult />} />}
					{lowerCaseModules.includes(ESRD_MODEL.toLowerCase()) && <Route exact path="/pricer/esrd" element={<ESRD />} />}
					{lowerCaseModules.includes(ESRD_MODEL.toLowerCase()) && <Route exact path="/pricer/esrd/result" element={<ESRDResult />} />}
					{lowerCaseModules.includes(HH_MODEL.toLowerCase()) && <Route exact path="/pricer/hh" element={<HH />} />}
					{lowerCaseModules.includes(HH_MODEL.toLowerCase()) && <Route exact path="/pricer/hh/result" element={<HHResult />} />}
					{lowerCaseModules.includes(SNF_MODEL.toLowerCase()) && <Route exact path="/pricer/snf" element={<SNF />} />}
					{lowerCaseModules.includes(SNF_MODEL.toLowerCase()) && <Route exact path="/pricer/snf/result" element={<SNFResult />} />}
					{enableFQHCPricer && lowerCaseModules.includes(FQHC_MODEL.toLowerCase()) && <Route exact path="/pricer/fqhc" element={<FQHC />} />}
					{enableFQHCPricer && lowerCaseModules.includes(FQHC_MODEL.toLowerCase()) && <Route exact path="/pricer/fqhc/result" element={<FQHCResult />} />}
					{enableBatchDashboard && <Route exact path="/upload/batch" element={<BatchSubmit />} />}
					{enableEOB && <Route exact path="/EOB" element={<Protect module={EOB_VIEW} behavior={REDIRECT}><EobGenerator /></Protect>} />}
					<Route exact path="*" element={<NotFound />} />
				</Routes>
			);
		} else {
			return (
				<Routes>
					<Route exact path="/" element={<Home />} />
					<Route exact path="/errors/unauthorized" element={<Unauthorized />} />
					<Route exact path="/pricer/ip/acute" element={<IPAcute />} />
					<Route exact path="/pricer/ip/acute/result" element={<IPAcuteResult />} />
					<Route exact path="/pricer/ip/psych" element={<IPPysch />} />
					<Route exact path="/pricer/ip/psych/result" element={<IPPyschResult />} />
					<Route exact path="/pricer/ip/rehab" element={<IPRehab />} />
					<Route exact path="/pricer/ip/rehab/result" element={<IPRehabResult />} />
					<Route exact path="/pricer/ltch" element={<LTCH />} />
					<Route exact path="/pricer/ltch/result" element={<LTCHResult />} />
					<Route exact path="/pricer/opps" element={<OPPS />} />
					<Route exact path="/pricer/opps/result" element={<OPPSResult />} />
					<Route exact path="/pricer/pfs" element={<PFS />} />
					<Route exact path="/pricer/pfs/result" element={<PFSResult />} />
					<Route exact path="/pricer/esrd" element={<ESRD />} />
					<Route exact path="/pricer/esrd/result" element={<ESRDResult />} />
					<Route exact path="/pricer/hh" element={<HH />} />
					<Route exact path="/pricer/hh/result" element={<HHResult />} />
					<Route exact path="/pricer/snf" element={<SNF />} />
					<Route exact path="/pricer/snf/result" element={<SNFResult />} />
					{enableFQHCPricer &&  <Route exact path="/pricer/fqhc" element={<FQHC />} />}
					{enableFQHCPricer && <Route exact path="/pricer/fqhc/result" element={<FQHCResult />} />}
					{enableBatchDashboard && <Route exact path="/upload/batch" element={<BatchSubmit />} />}
					{enableEOB && <Route exact path="/EOB" element={<Protect module={EOB_VIEW} behavior={REDIRECT}><EobGenerator /></Protect>} />}
					<Route exact path="*" element={<NotFound />} />
				</Routes>
			);
		}
	};

	return (<>
		<header>
			<div className="container logo-container">
				<div className="row justify-content-between align-items-center">
					<div className="col-auto">
						<Link to="/">
							<img src="/images/Milliman logo_white.png" className="logo" />
						</Link>
					</div>
					<div className="col-auto">
						<div className="row align-items-center">
							{authEnabled && <AuthenticatedTemplate>
								<div className="col-auto">
									<SignOutButton className="rounded-pill" />
								</div>
							</AuthenticatedTemplate>}
						</div>
					</div>
				</div>
			</div>
		</header>

		<div className="container p-2 main">
			{renderContent()}
		</div>
	</>
	);
}

export {
	App,
	ModulesContext
};
